import React, { Component, Suspense, useEffect } from 'react'
import { Router, Location, navigate } from '@reach/router'
import GoogleAnalytics from 'react-ga'
import { datadogLogs } from '@datadog/browser-logs'
import dayjs from 'dayjs'
import 'dayjs/locale/da'

import i18n from './i18n'
import { detectLocaleFromPath, localePath } from './helpers/locale'

import Authentication from './Authentication'
import Header from './Header'
import Footer from './Footer'
import Spinner from './Spinner'
import AccountList from './AccountList'
import TenancyList from './TenancyList'
import TrackPageViews from './TrackPageViews'
import NotFound from './NotFound'

import { fetchLoggedInUser } from './helpers/api'

import {
  setActiveApiKey,
  setAccountList,
  setShowLoginModal,
} from './state/accounts'
import { setUser } from './state/user'

// Lazy loaded components
const AccountActivation = React.lazy(() =>
  import(/* webpackChunkName: "AccountActivation" */ './AccountActivation')
)
const AccountEnforcer = React.lazy(() =>
  import(/* webpackChunkName: "AccountEnforcer" */ './AccountEnforcer')
)
const Household = React.lazy(() =>
  import(/* webpackChunkName: "Household" */ './Household')
)
const ResetPassword = React.lazy(() =>
  import(/* webpackChunkName: "ResetPassword" */ './ResetPassword')
)

// Keeps i18next and dayjs in sync with the locale detected from the URL.
// The persisted language preference is deliberately not touched here:
// it only records explicit choices (the language menu in the header), so passively
// following a link in the other language does not overwrite it.
const LocaleSync = ({ locale }) => {
  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
    dayjs.locale(locale)
    document.documentElement.lang = locale
    // Page views are tracked without the /en prefix so page stats stay
    // comparable; this event provides the language axis instead
    GoogleAnalytics.event({
      category: 'Language',
      action: 'Active',
      label: locale,
      nonInteraction: true,
    })
  }, [locale])
  return null
}

/* Main component ----------------------------------------------------------- */
class App extends Component {
  constructor(props) {
    super(props)
    // Returning users who explicitly chose English are sent to /en before the
    // first render. Danish remains the default on unprefixed routes.
    try {
      const storedLanguage = localStorage.getItem('language')
      const { pathname, search, hash } = window.location
      if (storedLanguage === 'en' && detectLocaleFromPath(pathname) !== 'en') {
        navigate(localePath(pathname, 'en') + search + hash, { replace: true })
      }
    } catch (error) {
      datadogLogs.logger.warn(`App.constructor: ${error.message}`)
    }
  }

  async componentDidMount() {
    try {
      // Hydrate the global accounts state from localStorage
      const activeApiKey = localStorage.getItem('activeApiKey')
      const accountList = JSON.parse(localStorage.getItem('accountList'))
      if (activeApiKey && accountList && accountList.length > 0) {
        setActiveApiKey(activeApiKey)
        setAccountList(accountList)
        setShowLoginModal(false)
        datadogLogs.logger.info(
          'App.componentDidMount: Restored session from localStorage'
        )
      } else {
        datadogLogs.logger.info(
          'App.componentDidMount: Initiated new demo session'
        )
      }
      // Load required data
      const user = await fetchLoggedInUser()
      setUser(user)
    } catch (error) {
      datadogLogs.logger.error(`App.componentDidMount: ${error.message}`)
    }
  }

  componentDidCatch(error = {}, info = {}) {
    datadogLogs.logger.error(`${error.message}, ${info.componentStack}`)
  }

  render() {
    return (
      <Location>
        {({ location }) => {
          const locale = detectLocaleFromPath(location.pathname)
          return (
            <div className="min-vh-100">
              <LocaleSync locale={locale} />
              <Header />
              <Suspense fallback={<Spinner className="mv4" />}>
                <Router
                  basepath={locale === 'en' ? '/en' : '/'}
                  component="main"
                  role="main"
                  className="w-100 mw8 mh3-m center-l ph3 ph4-l pv2 pv3-m pv4-l flex-grow-1"
                >
                  <AccountList path="/" />
                  <AccountList path="/account" />
                  <AccountEnforcer path="/account/:accountId">
                    <TenancyList path="/" />
                    <TenancyList path="household" />
                    <Household path="household/:householdId" />
                    <NotFound default />
                  </AccountEnforcer>
                  <ResetPassword path="/password-reset/:token" />
                  <AccountActivation path="/activate/:token" />
                  <NotFound default />
                </Router>
              </Suspense>
              <Authentication />
              <Footer />
              <TrackPageViews />
            </div>
          )
        }}
      </Location>
    )
  }
}

export default App
